/* CUSTOM_MARKETINGOPS_BY_CLAUDE — Site-wide UTM journey capture (Phase 1.0)
 *
 * Loaded site-wide from layout/theme.liquid. On every page load that carries
 * any UTM param or ad-platform click ID, append a touch to a single
 * localStorage array (bsfs_utm_journey). Dedup on (source|medium|campaign)
 * against the last entry, cap at 20 entries, trim entries older than 90 days
 * at write time.
 *
 * Safari ITP nukes localStorage after 7 days of site inactivity. The worker
 * mirrors the journey into an HttpOnly first-party cookie on every
 * /lead-intake success; this script falls back to /utm-restore once per
 * session if localStorage came back empty, repopulating it from that cookie.
 *
 * Server-side attribution (firstPaidTouch / lastPaidTouch / assistedConversion)
 * is computed by the rfq-worker at conversion time from the full journey
 * payload — see research/04-utm-attribution-patterns.md Patterns 1 + 4.
 *
 * Privacy: URL params only; no PII; localStorage of first-party marketing
 * data only.
 */
(function () {
  'use strict';

  var KEY = 'bsfs_utm_journey';
  var SESSION_RESTORE_FLAG = 'bsfs_utm_restore_tried';
  var MAX = 20;
  var TTL_DAYS = 90;
  var RESTORE_URL = 'https://rfq-worker.ryan-29b.workers.dev/utm-restore';

  var TRACK_PARAMS = [
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
    'gclid', 'fbclid', 'msclkid', 'ttclid', 'li_fat_id',
    // CUSTOM_MARKETINGOPS_BY_CLAUDE — Google privacy-safe click IDs + auto-tag
    // campaign ID (gad_campaignid resolves a click straight to its campaign
    // without the gclid->click_view API hop, which can't resolve gbraid/wbraid).
    'gbraid', 'wbraid', 'gad_campaignid', 'gad_source',
    // CUSTOM_MARKETINGOPS_BY_CLAUDE (2026-07-23) — OpenAI Ads click ID (their gclid
    // equivalent, auto-appended to the landing URL as ?oppref=...). Without it an OpenAI
    // ad click that lost its utm params (ad-blocker / redirect) captures no paid touch
    // → the lead reads 'organic'. Keep in sync with PAID_CLICK_ID_KEYS in lead-routes.js
    // and the channel classifiers in handlers/admin.js.
    'oppref'
  ];

  function readJourney() {
    try {
      var raw = localStorage.getItem(KEY);
      if (!raw) return [];
      var arr = JSON.parse(raw);
      return Array.isArray(arr) ? arr : [];
    } catch (e) { return []; }
  }

  function writeJourney(arr) {
    try { localStorage.setItem(KEY, JSON.stringify(arr)); } catch (e) {}
  }

  function trimByTtl(journey) {
    var cutoff = Date.now() - (TTL_DAYS * 86400 * 1000);
    return journey.filter(function (t) {
      var ts = t && t.ts ? new Date(t.ts).getTime() : 0;
      return ts > cutoff;
    });
  }

  function dedupSig(t) {
    return (t.utm_source || '') + '|' + (t.utm_medium || '') + '|' + (t.utm_campaign || '');
  }

  function captureCurrentTouch() {
    var touch = {};
    var any = false;
    try {
      var params = new URL(location.href).searchParams;
      TRACK_PARAMS.forEach(function (k) {
        var v = params.get(k);
        if (v) { touch[k] = v; any = true; }
      });
    } catch (e) { return; }
    if (!any) return;

    touch.ts = new Date().toISOString();
    touch.referrer = (document.referrer || '').slice(0, 500);
    touch.landingPath = (location.pathname + location.search).slice(0, 300);

    var journey = trimByTtl(readJourney());
    var last = journey[journey.length - 1];
    if (last && dedupSig(touch) === dedupSig(last)) {
      // Same campaign as the previous touch — keep the older ts but refresh
      // landing path so we know the most recent page they entered on.
      last.landingPath = touch.landingPath;
      last.referrer = touch.referrer;
      writeJourney(journey);
      return;
    }

    journey.push(touch);
    if (journey.length > MAX) journey = journey.slice(-MAX);
    writeJourney(journey);
  }

  // Safari ITP escape hatch: if localStorage came back empty, the worker may
  // still hold the journey in an HttpOnly cookie that was set on a prior
  // /lead-intake. Pull it back ONCE per browser session. Skip if a fresh
  // touch already landed this page-load.
  function tryRestoreFromWorker() {
    try {
      if (sessionStorage.getItem(SESSION_RESTORE_FLAG)) return;
      sessionStorage.setItem(SESSION_RESTORE_FLAG, '1');
    } catch (e) {}
    if (readJourney().length > 0) return;
    try {
      fetch(RESTORE_URL, { method: 'GET', credentials: 'include', mode: 'cors' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (data) {
          if (!data || !Array.isArray(data.journey) || !data.journey.length) return;
          if (readJourney().length > 0) return; // a touch landed mid-flight
          writeJourney(trimByTtl(data.journey));
        })
        .catch(function () {});
    } catch (e) {}
  }

  // CUSTOM_MARKETINGOPS_BY_CLAUDE — Reusable read API so non-contact-form lead
  // surfaces (Add-to-Quote / custom-quote.js, chatbot / custom-chatbot.js) can
  // attach the same site-wide journey to their own POSTs without each
  // re-implementing the parse/dedup/TTL logic. The contact-form interceptor
  // keeps its own inline reader (already live + working) — this is additive.
  function gaClientIdFromCookie() {
    try {
      var m = document.cookie.match(/(?:^|;\s*)_ga=([^;]+)/);
      if (!m) return null;
      var parts = decodeURIComponent(m[1]).split('.');
      if (parts.length < 4) return null;
      return parts.slice(2).join('.');
    } catch (e) { return null; }
  }
  function utmSummaryFromJourney(journey) {
    var last = (journey && journey.length) ? journey[journey.length - 1] : {};
    var utm = {};
    if (last.utm_source)   utm.source   = last.utm_source;
    if (last.utm_medium)   utm.medium   = last.utm_medium;
    if (last.utm_campaign) utm.campaign = last.utm_campaign;
    if (last.utm_term)     utm.term     = last.utm_term;
    if (last.utm_content)  utm.content  = last.utm_content;
    return utm;
  }
  window.BSFSJourney = {
    read: function () { return trimByTtl(readJourney()); },
    utmSummary: function () { return utmSummaryFromJourney(trimByTtl(readJourney())); },
    gaClientId: gaClientIdFromCookie
  };

  captureCurrentTouch();
  tryRestoreFromWorker();
})();
